import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories, articles } from "@/mocks/data";

const categoryImages: Record<string, string> = {
  "cat-1": "https://readdy.ai/api/search-image?query=Soft%20romantic%20abstract%20background%20with%20registration%20theme%20warm%20pink%20and%20gold%20gradients%20minimalist%20style%20clean%20composition&width=800&height=500&seq=cat-registration-01&orientation=landscape",
  "cat-2": "https://readdy.ai/api/search-image?query=Gentle%20abstract%20tutorial%20theme%20with%20soft%20coral%20gradients%20subtle%20glow%20effects%20minimalist%20design%20elegant%20atmosphere&width=800&height=500&seq=cat-feature-01&orientation=landscape",
  "cat-3": "https://readdy.ai/api/search-image?query=Soft%20shield%20security%20theme%20abstract%20with%20rose%20gold%20gradients%20floating%20light%20particles%20minimalist%20elegant%20style&width=800&height=500&seq=cat-safety-01&orientation=landscape",
  "cat-4": "https://readdy.ai/api/search-image?query=Romantic%20heart%20theme%20abstract%20with%20warm%20peach%20and%20rose%20gradients%20delicate%20floating%20hearts%20minimalist%20design&width=800&height=500&seq=cat-dating-01&orientation=landscape",
  "cat-5": "https://readdy.ai/api/search-image?query=Soft%20modern%20news%20theme%20abstract%20with%20warm%20golden%20gradients%20clean%20minimalist%20style%20elegant%20simple&width=800&height=500&seq=cat-news-01&orientation=landscape",
  "cat-6": "https://readdy.ai/api/search-image?query=Gentle%20FAQ%20question%20mark%20theme%20abstract%20with%20soft%20coral%20and%20rose%20gradients%20minimalist%20composition&width=800&height=500&seq=cat-faq-01&orientation=landscape",
};

export default function CategoryPage() {
  const [searchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") || "all";
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    window.scrollTo(0, 0);
  }, [activeCat]);

  const filteredArticles =
    activeCat === "all"
      ? articles
      : articles.filter(
          (a) => categories.find((c) => c.slug === activeCat)?.id === a.categoryId
        );

  const activeCategory = categories.find((c) => c.slug === activeCat);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: activeCategory
              ? `${activeCategory.name} - 爱情岛网站`
              : "全部分类 - 爱情岛网站",
            description: activeCategory
              ? activeCategory.description
              : "浏览爱情岛网站全部文章分类，包括注册指南、功能教程、安全指南、交友技巧等。",
            url: `https://www.aiqingdao.com/category${activeCat !== "all" ? `?cat=${activeCat}` : ""}`,
            isPartOf: {
              "@type": "WebSite",
              name: "爱情岛网站",
              url: "https://www.aiqingdao.com/",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://www.aiqingdao.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: activeCategory ? activeCategory.name : "全部分类",
              },
            ],
          }),
        }}
      />
      <Navbar />
      <main className="pt-16">
        <section className="py-12 md:py-16 bg-background-100">
          <div className="container-page">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                首页
              </Link>
              <span>/</span>
              <span className="text-foreground-900">
                {activeCategory ? activeCategory.name : "全部分类"}
              </span>
            </nav>

            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              {activeCategory ? activeCategory.name : "全部分类"}
            </h1>
            {activeCategory && (
              <p className="text-foreground-600 max-w-2xl leading-relaxed">
                {activeCategory.description}
              </p>
            )}
          </div>
        </section>

        <section className="py-10 bg-background-50">
          <div className="container-page">
            <div className="flex flex-wrap gap-2 mb-10">
              <Link
                to="/category"
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                  activeCat === "all"
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category?cat=${cat.slug}`}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                    activeCat === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {mounted && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={categoryImages[article.categoryId] || categoryImages["cat-1"]}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={article.title}
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-background-50/90 text-primary-500">
                          {article.categoryName}
                        </span>
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2.5 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4 flex-1">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {mounted && filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}